<script setup>
/**
 * HR Admin login page — Firebase email/password.
 * Replaces the MSAL-based login from the Materio template.
 * Route meta (layout: blank, public: true) is set in additional-routes.js.
 */
import { ref } from "vue";
import { useAuthStore } from "@/stores/auth";

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();

const email = ref("");
const password = ref("");
const showPassword = ref(false);
const submitting = ref(false);
const errorMsg = ref("");

async function submit() {
  errorMsg.value = "";
  submitting.value = true;
  try {
    await auth.signIn(email.value, password.value);
    const redirect = route.query.redirect;
    router.push(redirect && redirect !== "/" ? redirect : { name: "dashboard" });
  } catch {
    errorMsg.value = auth.error ?? "Login failed. Please try again.";
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard class="auth-card pa-4 pt-7" max-width="448">
      <VCardItem class="justify-center">
        <VCardTitle class="text-h4 mb-1">HR Admin</VCardTitle>
        <VCardSubtitle>Sign in to your account</VCardSubtitle>
      </VCardItem>

      <VCardText class="pt-2">
        <VAlert
          v-if="errorMsg"
          color="error"
          variant="tonal"
          class="mb-4"
          closable
          @click:close="errorMsg = ''"
        >
          {{ errorMsg }}
        </VAlert>

        <VForm @submit.prevent="submit">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="email"
                label="Email"
                type="email"
                placeholder="admin@example.com"
                :disabled="submitting"
                autofocus
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="password"
                label="Password"
                placeholder="············"
                :type="showPassword ? 'text' : 'password'"
                :append-inner-icon="showPassword ? 'mdi:eye-off-outline' : 'mdi:eye-outline'"
                :disabled="submitting"
                @click:append-inner="showPassword = !showPassword"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                color="primary"
                :loading="submitting"
                :disabled="!email || !password"
              >
                Sign In
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.auth-wrapper {
  min-block-size: 100dvh;
  background-color: rgb(var(--v-theme-background));
}
</style>
